<template>
  <v-dialog
    :model-value="open"
    max-width="900"
    scrollable
    class="gr-overlay"
    @update:model-value="$emit('close')"
  >
    <div class="gr-docdlg">
      <header class="gr-docdlg__head">
        <div class="gr-docdlg__head-copy">
          <p class="gr-docdlg__eyebrow">Dokumentasi kegiatan</p>
          <h2 class="gr-docdlg__title">
            {{ detail?.title || summary?.title || "Memuat dokumentasi…" }}
          </h2>
          <p v-if="metaLine" class="gr-docdlg__meta">{{ metaLine }}</p>
        </div>
        <button
          type="button"
          class="gr-docdlg__close"
          aria-label="Tutup dokumentasi"
          @click="$emit('close')"
        >
          <v-icon icon="mdi-close" size="20" aria-hidden="true" />
        </button>
      </header>

      <div class="gr-docdlg__body">
        <p v-if="pending" class="gr-docdlg__status" aria-live="polite">
          Memuat dokumentasi…
        </p>

        <p v-else-if="errorMessage" class="gr-docdlg__error" role="alert">
          <span>{{ errorMessage }}</span>
          <button type="button" class="gr-btn gr-btn--outline" @click="load">
            Coba lagi
          </button>
        </p>

        <template v-else-if="detail">
          <!-- Photographs first: this is an evidence archive, so the frames are
               the record and the note explains them. -->
          <div v-if="photos.length" class="gr-docdlg__sheet">
            <figure
              v-for="photo in photos"
              :key="photo.uuid"
              class="gr-docdlg__frame"
            >
              <img
                v-if="!failed.has(photo.uuid)"
                :src="photo.imageUrl"
                :alt="photo.altText"
                class="gr-docdlg__img"
                loading="lazy"
                @error="markFailed(photo.uuid)"
              />
              <div
                v-else
                class="gr-docdlg__fallback"
                aria-label="Gambar tidak tersedia"
              >
                <v-icon
                  icon="mdi-image-off-outline"
                  size="24"
                  aria-hidden="true"
                />
              </div>
            </figure>
          </div>
          <p v-else class="gr-docdlg__status">
            Dokumentasi ini belum memiliki foto.
          </p>

          <p v-if="detail.description" class="gr-docdlg__description">
            {{ detail.description }}
          </p>
        </template>
      </div>
    </div>
  </v-dialog>
</template>

<script lang="ts" setup>
import {
  formatDocumentationDate,
  getDocumentationDetail,
  type DocumentationResponse,
  type PublicDocumentation,
} from "~/features/documentation/documentation";

const props = defineProps<{
  open: boolean;
  uuid: string | null;
  /** The row already on screen, so the dialog has a title before the fetch lands. */
  summary: PublicDocumentation | null;
}>();

defineEmits<{ close: [] }>();

const detail = ref<PublicDocumentation | null>(null);
const pending = ref(false);
const errorMessage = ref("");
const failed = ref(new Set<string>());

const photos = computed(() => detail.value?.photos ?? []);

const metaLine = computed(() => {
  const row = detail.value ?? props.summary;
  if (!row) return "";
  return [
    row.documentationDate ? formatDocumentationDate(row.documentationDate) : "",
    row.documentationLocation,
    // Kept as text: the "Lihat aktivitasnya" link is gone, but which activity a
    // record belongs to is still part of the record.
    row.activity?.title,
  ]
    .filter(Boolean)
    .join(" · ");
});

const markFailed = (uuid: string): void => {
  failed.value = new Set(failed.value).add(uuid);
};

async function load(): Promise<void> {
  if (!props.uuid) return;
  pending.value = true;
  errorMessage.value = "";
  detail.value = null;
  failed.value = new Set();
  try {
    const { $customFetch } = useNuxtApp();
    const response = await $customFetch<DocumentationResponse>(
      `/public-documentations/${encodeURIComponent(props.uuid)}`,
    );
    detail.value = getDocumentationDetail(response);
    if (!detail.value) errorMessage.value = "Dokumentasi tidak ditemukan.";
  } catch {
    errorMessage.value = "Dokumentasi belum dapat dimuat.";
  } finally {
    pending.value = false;
  }
}

// Fetch when the dialog opens, and again if a different row is opened.
watch(
  () => [props.open, props.uuid],
  ([isOpen]) => {
    if (isOpen) void load();
  },
  { immediate: true },
);
</script>

<style scoped>
.gr-docdlg {
  display: flex;
  flex-direction: column;
  max-height: 88vh;
  border-radius: 4px;
  background-color: var(--gr-surface);
}

.gr-docdlg__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px 16px;
  border-bottom: 1px solid var(--gr-line);
}

.gr-docdlg__eyebrow {
  margin: 0 0 6px;
  color: var(--gr-green);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  line-height: 1;
  text-transform: uppercase;
}

.gr-docdlg__title {
  margin: 0;
  color: var(--gr-ink);
  font-size: clamp(1.15rem, 1rem + 0.8vw, 1.6rem);
  font-weight: 700;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.gr-docdlg__meta {
  margin: 8px 0 0;
  color: var(--gr-ink-3);
  font-size: 0.875rem;
  line-height: 1.5;
}

.gr-docdlg__close {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  margin: -10px -10px 0 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--gr-ink-2);
  cursor: pointer;
}

.gr-docdlg__body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 20px 22px;
}

.gr-docdlg__description {
  max-width: 70ch;
  margin: 20px 0 0;
  color: var(--gr-ink-2);
  font-size: 1rem;
  line-height: 1.7;
  white-space: pre-line;
}

/* One frame per row at its own aspect ratio rather than a cropped grid: a
   documentation photo is the evidence, so it is not cropped to fit a tile. */
.gr-docdlg__sheet {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.gr-docdlg__frame {
  display: flex;
  justify-content: center;
  margin: 0;
  overflow: hidden;
  border-radius: 4px;
  background-color: var(--gr-green-tint);
}

.gr-docdlg__img {
  display: block;
  width: auto;
  max-width: 100%;
  max-height: 600px;
  object-fit: contain;
}

.gr-docdlg__fallback {
  display: grid;
  place-items: center;
  width: 100%;
  height: 220px;
  color: var(--gr-ink-3);
}

.gr-docdlg__status {
  margin: 0;
  color: var(--gr-ink-3);
  font-size: 0.9375rem;
}

.gr-docdlg__error {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  margin: 0;
  padding: 14px;
  border: 1px solid var(--gr-danger);
  border-radius: 4px;
  background-color: rgba(179, 38, 30, 0.06);
  color: var(--gr-danger);
  font-size: 0.9375rem;
}
</style>
